import { createFileRoute } from '@tanstack/react-router';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderMobileChatButton,
} from '@/app/ui/components/header';
import { H1 } from '@/app/ui/components/heading';
import {
  MainContainer,
  MainHeader,
  MainHeaderBackButton,
  MainScrollArea,
} from '@/app/ui/components/main';
import { Sidenav, SidenavItem, SidenavLink, SidenavList } from '@/app/ui/components/sidenav';
import { EvaluationTypeLabelsEnum } from '@/app/utils/constants';
import { SettingsIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { Outlet } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout/(home)/settings/_layout',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations();
  const tContract = useTranslations('pages.contractHome');
  const { projectId, contractId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu />
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                <span className="line-clamp-1">{project.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={SettingsIcon} />
                {tContract('nav.settings')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderMobileChatButton />
      </Header>
      <MainScrollArea>
        <MainHeader>
          <H1>
            <MainHeaderBackButton />
            {t('pages.projectSettings.title')}
          </H1>
        </MainHeader>
        <div className="flex max-lg:flex-col">
          <Sidenav className="md:sticky top-0 h-fit">
            <SidenavList className="p-4">
              <SidenavItem>
                <SidenavLink
                  to="/project/$projectId/contracts/$contractId/settings"
                  params={{ projectId, contractId }}
                >
                  {t('general.general')}
                </SidenavLink>
              </SidenavItem>
            </SidenavList>
          </Sidenav>
          <MainContainer className="pt-3">
            <Outlet />
          </MainContainer>
        </div>
      </MainScrollArea>
    </>
  );
}
